/** The `admin` area: the operator's Admin pages. Owned by the stage 2 slot
 *  people (docs/I18N.md §11). The keys read in the page's order: the frame,
 *  the figures over every tab, Usage, Accounts, Links. */
export const admin = {
  // --- Frame (layout.tsx, admin-tabs.tsx, admin-dock.tsx) ---
  'admin.title': 'Admin',
  'admin.tabs.label': 'Admin section',
  'admin.tab.usage': 'Usage',
  'admin.tab.accounts': 'Accounts',
  'admin.tab.links': 'Links',
  /** The compact dock's way back to Work. */
  'admin.dock.home': 'Home',
  'admin.dock.newLink': 'New link',
  /** A group role as a link's seat names it: lowercase, the stored word. */
  'admin.role.viewer': 'viewer',
  'admin.role.member': 'member',
  'admin.role.owner': 'owner',

  // --- The figures over every tab (figures.tsx) ---
  /** The band's accessible name. */
  'admin.figures.label': 'The instance in figures',
  /** Each figure: the name beside its number, its meaning on hover, and the
   *  quiet line beneath. */
  'admin.figures.accounts': 'accounts',
  'admin.figures.accountsHint': 'Every person with an account on this instance.',
  'admin.figures.accountsSub': {
    one: '{count} operator · {unverified} unverified · {deleted} deleted',
    other: '{count} operators · {unverified} unverified · {deleted} deleted',
  },
  'admin.figures.seenToday': 'seen today',
  'admin.figures.seenTodayHint': 'Accounts whose session was used today (UTC).',
  'admin.figures.seenTodaySub': '{week} in 7 days · {month} in 30 days',
  'admin.figures.items': 'items',
  'admin.figures.itemsHint': 'Work items on the instance, closed ones included.',
  'admin.figures.events': 'events',
  'admin.figures.eventsHint': 'Every act on the log since the instance began.',
  'admin.figures.eventsSub': '{events} in {days} days · {count} sign-ins',

  // --- Usage (usage/page.tsx, usage-chart.tsx) ---
  'admin.usage.lastDays': 'Last {days} days',
  'admin.usage.lastDaysHint': 'UTC. Seen is a session; active is an act on the log.',
  /** The three measures, as the charts and the table head name them. */
  'admin.usage.measure.signIns': 'Sign-ins',
  'admin.usage.measure.events': 'Events',
  'admin.usage.measure.activeAccounts': 'Active accounts',
  /** One chart: its total, its accessible name, a bar's tooltip, the axis end. */
  'admin.usage.chart.total': '{total} in {count} days',
  'admin.usage.chart.label': '{measure} per day, last {count} days',
  'admin.usage.bar.signIns': '{day}: {count} sign-ins',
  'admin.usage.bar.events': '{day}: {count} events',
  'admin.usage.bar.activeAccounts': '{day}: {count} active accounts',
  'admin.usage.chart.today': 'today',
  /** The numbers fold. */
  'admin.usage.numbers': 'Numbers',
  'admin.usage.byDay': 'By day',
  'admin.usage.table.day': 'Day',
  'admin.usage.table.active': 'Active',

  // --- Accounts (accounts/page.tsx, accounts.tsx) ---
  /** The content head: the filter field and the deleted toggle. */
  'admin.accounts.filter': 'Filter accounts',
  'admin.accounts.filterPlaceholder': 'Filter accounts…',
  'admin.accounts.showDeleted': 'Show deleted',
  'admin.accounts.hideDeleted': 'Hide deleted',
  'admin.accounts.noMatch': 'No account matches.',
  /** The list's head row; Last seen says what it is measured by on hover. */
  'admin.accounts.head.name': 'Name',
  'admin.accounts.head.address': 'Address',
  'admin.accounts.head.lastSeen': 'Last seen',
  'admin.accounts.head.lastSeenTip': 'Last seen is the session, never what someone looked at.',
  'admin.accounts.head.created': 'Created',
  'admin.accounts.head.sessions': 'Sessions',
  'admin.accounts.head.events': 'Events',
  'admin.accounts.head.keys': 'Keys',
  /** A row: the viewer's own account, the badges and their meaning. */
  'admin.accounts.you': 'you',
  'admin.accounts.operator': 'operator',
  'admin.accounts.operatorTip': 'Runs this instance: sees usage and accounts, mints links.',
  'admin.accounts.unverified': 'unverified',
  'admin.accounts.unverifiedTip':
    'Address not verified yet. A verification link can be minted from its menu.',
  'admin.accounts.scrubbed': 'scrubbed',
  'admin.accounts.scrubbedTip':
    'Deleted, and its personal data removed. Nothing can be done with it.',
  'admin.accounts.deleted': 'deleted',
  'admin.accounts.deletedTip':
    'Deleted. It can be restored from its menu until the scrub removes its personal data.',
  'admin.accounts.scrubOn': 'scrub {date}',
  'admin.accounts.never': 'never',
  /** The row's menu trigger. */
  'admin.accounts.acts': 'Acts for {name}',
  /** The details: one fact per row. */
  'admin.account.address': 'Address',
  'admin.account.verified': 'Verified',
  'admin.account.operator': 'Operator',
  'admin.account.yes': 'yes',
  'admin.account.no': 'no',
  'admin.account.created': 'Created',
  'admin.account.lastSeen': 'Last seen',
  'admin.account.sessions': 'Sessions',
  'admin.account.sessionsValue': '{live} live of {count}',
  'admin.account.events': 'Events',
  'admin.account.eventsValue': "{count} carried by this person's authority",
  'admin.account.keys': 'Agent keys',
  'admin.account.keysValue': '{count} live',
  'admin.account.deleted': 'Deleted',
  'admin.account.deletedBySelf': '{date} by themselves',
  'admin.account.deletedBy': '{date} by {name}',
  'admin.account.deletedByOperator': '{date} by an operator',
  'admin.account.scrub': 'Scrub',
  'admin.account.scrubDone': 'done {date}',
  'admin.account.scrubDue': 'due {date}, unless restored',
  'admin.account.yourAccount': 'your account',
  /** The acts, one verb per row of the account's menu. */
  'admin.account.copyAddress': 'Copy address',
  'admin.account.restore': 'Restore',
  'admin.account.resetLink': 'Reset link',
  'admin.account.verifyLink': 'Verify link',
  'admin.account.makeOperator': 'Make operator',
  'admin.account.removeOperator': 'Remove operator',
  'admin.account.delete': 'Delete',
  /** A minted link, shown once in its own dialog (titled by its act). */
  'admin.account.copy': 'Copy',
  'admin.account.copied': 'Copied',
  'admin.account.done': 'OK',
  'admin.account.resetLinkHint':
    'Password reset link. The code in it expires in a few minutes; hand it over now.',
  'admin.account.verifyLinkHint':
    'Email verification link. The code in it expires in a few minutes; hand it over now.',
  /** Delete's confirmation: what ends now for good, what a restore brings back
   *  during the grace period, and what the scrub removes after it. */
  'admin.account.deleteTitle': "Delete {name}'s account",
  'admin.account.deleteNow':
    'Ended now, for good: every session on every device, every agent key, and notifications to every device. A restore does not bring these back; the person signs in again and mints new keys.',
  'admin.account.deleteGrace':
    'For {days} days a restore brings back the account itself: its name, address, ways to sign in and everything it holds in the work.',
  'admin.account.deleteScrub':
    'After that the scrub removes the personal data, and nothing can be restored.',
  'admin.account.deleteCancel': 'Cancel',
  'admin.account.deleteConfirm': 'Delete account',

  // --- Links (links.tsx, new-link.tsx) ---
  'admin.links.new': 'New link',
  'admin.links.newHint': 'Creates an account; with a group, also seats the newcomer there.',
  /** The New-link form. */
  'admin.links.label': 'Label',
  'admin.links.labelPlaceholder': 'Who this is for',
  'admin.links.uses': 'Uses',
  'admin.links.expiresInDays': 'Expires in days',
  'admin.links.seatInGroup': 'Seat in group',
  'admin.links.noGroup': 'No group (account only)',
  'admin.links.role': 'Role',
  'admin.links.groupRole': 'Group role',
  'admin.links.create': 'Create link',
  /** The link, shown once after minting, in the same dialog. */
  'admin.links.copy': 'Copy',
  'admin.links.copied': 'Copied',
  'admin.links.done': 'OK',
  'admin.links.shownOnce':
    'Shown once, for “{label}”. Only a hash is stored; if this is lost, make a new link.',
  /** The content head: the filter field and the toggle for spent links
   *  (expired, used up or revoked). */
  'admin.links.filter': 'Filter links',
  'admin.links.filterPlaceholder': 'Filter links…',
  'admin.links.showExpired': 'Show {count} expired',
  'admin.links.hideExpired': 'Hide {count} expired',
  'admin.links.noMatch': 'No link matches.',
  'admin.links.noneOpen': 'No open links.',
  /** The list's head row and its rows. */
  'admin.links.head.label': 'Label',
  'admin.links.head.seat': 'Group',
  'admin.links.head.uses': 'Uses',
  'admin.links.head.expires': 'Expires',
  'admin.links.head.madeBy': 'Made by',
  'admin.links.head.usedBy': 'Used by',
  'admin.links.empty': 'No links yet.',
  'admin.links.seat': '{group} · {role}',
  'admin.links.accountOnly': 'account only',
  'admin.links.usesTip': '{used} of {count} uses taken',
  /** The Used by cell: the people who joined through it, as a list. */
  'admin.links.usedByNames': '{names}',
  /** A use by an account that has since been removed. */
  'admin.links.removedAccount': 'removed account',
  /** The row's menu trigger and its one act. */
  'admin.links.acts': 'Acts for {label}',
  'admin.links.revoke': 'Revoke',
  /** A spent link's status chip, and what it means. */
  'admin.links.status.exhausted': 'exhausted',
  'admin.links.status.expired': 'expired',
  'admin.links.status.revoked': 'revoked',
  'admin.links.exhaustedTip': {
    one: 'All {count} use taken; nobody else can join through it.',
    other: 'All {count} uses taken; nobody else can join through it.',
  },
  'admin.links.expiredTip': 'Past its expiry ({date}); make a new link.',
  'admin.links.revokedTip': 'Revoked by an operator.',
  'admin.links.revokedOnTip': 'Revoked by an operator on {date}.',
} as const;
